import React, { useState, useEffect, useMemo, useRef } from "react";
import { View, TouchableOpacity, Platform, Image, } from "react-native";
import { manipulateAsync, SaveFormat } from "expo-image-manipulator";
import { translate } from "@clearabee/i18n";
import { MediaType } from "expo-media-library";
import { AssetsSelector } from "expo-images-picker";
import { Icon } from "../../../Icon/Icon";
import { Text } from "../../../Text/Text";
import { styles } from "./InputImage.styles";
import { Modal } from "../../../../Composite/Modal/Modal";
import { theme } from "../../../../theme";
const getBase64 = (file) => {
    const reader = new FileReader();
    return new Promise((resolve) => {
        reader.addEventListener("load", () => resolve(String(reader.result)));
        reader.readAsDataURL(file);
    });
};
const generateFileList = async (files) => {
    const promiseFiles = [];
    for (let i = 0; i < files.length; i += 1) {
        promiseFiles.push(getBase64(files[i]));
    }
    return Promise.all(promiseFiles).then((fileListBase64) => fileListBase64.map((base64, index) => {
        const { name, size, lastModified } = files[index];
        return {
            uri: base64,
            id: `${name}_${size}_${lastModified}`,
            file: files[index],
        };
    }));
};
const MultipleImagePicker = ({ toggleShowModal, setImages, currentSelection = [], maxSelection, }) => {
    const isAndroid = Platform.OS === "android";
    const imagePickerErrors = useMemo(() => ({
        errorTextColor: "black",
        errorMessages: {
            hasErrorWithPermissions: translate("common.pleaseAllowMediaPermissions"),
            hasErrorWithLoading: translate("common.errorUploadingImages"),
            hasErrorWithResizing: translate("common.errorUploadingImages"),
            hasNoAssets: translate("common.noImagesFound"),
        },
    }), []);
    const imagePickerSettings = useMemo(() => ({
        getImageMetaData: false,
        initialLoad: 20,
        assetsType: [MediaType.photo],
        minSelection: 1,
        maxSelection,
        portraitCols: 3,
        landscapeCols: 7,
    }), []);
    const imagePickerNavigator = useMemo(() => ({
        Texts: {
            finish: translate("common.finish"),
            back: translate("common.back"),
            selected: translate("common.selected"),
        },
        midTextColor: theme.colors.dark.base,
        minSelection: 1,
        buttonTextStyle: styles.buttonText,
        buttonStyle: styles.backAndFinishButton,
        onBack: () => {
            toggleShowModal(false);
        },
        onSuccess: (selectedImages) => {
            setImages(selectedImages);
            toggleShowModal(false);
        },
    }), []);
    const imagePickerStyles = useMemo(() => ({
        margin: theme.spacing.xsmall2,
        bgColor: theme.colors.light.base,
        spinnerColor: theme.colors.brand.base,
        widgetWidth: theme.spacing.xlarge4,
        videoIcon: {
            Component: () => React.createElement(Icon.Tick, { size: "large", color: "light" }),
            iconName: "clearabeeTick",
            color: "",
            bg: `${theme.colors.accent.base}50`,
            size: theme.spacing.large,
        },
        selectedIcon: {
            Component: () => React.createElement(Icon.Tick, { size: "large", color: "light" }),
            iconName: "clearabeeTick",
            color: "",
            bg: `${theme.colors.accent.base}50`,
            size: theme.spacing.large,
        },
    }), []);
    return (React.createElement(View, { testID: "multipleImagePickerContainer", style: [
            styles.assetContainerDefault,
            isAndroid && styles.androidAssetContainer,
        ] },
        React.createElement(AssetsSelector, { Settings: imagePickerSettings, Errors: imagePickerErrors, Styles: imagePickerStyles, Navigator: imagePickerNavigator, currentSelection: currentSelection })));
};
export const InputImage = ({ buttonLabel = "Upload image", buttonLabelStyles, buttonImage, buttonImageWidth, styles: userStyles, onChange, maxSelection = 8, modalStyles, as, smallThumbnail, initialImages = [], compress = 0.2, resize = { width: 1280 }, }) => {
    const [showModal, toggleShowModal] = useState(false);
    const [images, setImages] = useState(initialImages);
    const inputRef = useRef(null);
    const removeImage = (id) => {
        setImages(images.filter((image) => image.id !== id));
    };
    useEffect(() => {
        const files = images.map(({ uri, file }) => as === "file"
            ? Platform.OS === "web"
                ? file
                : { uri, type: "image/jpeg", name: file.filename }
            : uri);
        onChange?.(files);
    }, [images]);
    const handlePress = () => {
        if (Platform.OS === "web") {
            if (inputRef.current !== null && images.length < maxSelection) {
                inputRef.current.click();
            }
        }
        else {
            toggleShowModal(true);
        }
    };
    const handleWebChange = async (event) => {
        const files = event.target.files;
        const generatedFiles = files ? await generateFileList(files) : [];
        setImages([
            ...images.filter((file) => !generatedFiles.find(({ id }) => id === file.id)),
            ...generatedFiles,
        ].slice(0, maxSelection));
        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };
    const handleNativeChange = async (images) => {
        setImages(await Promise.all(images.map(async (image) => {
            if (image.uri.startsWith("ph://")) {
                image.uri = `assets-library://asset/${image.filename}?id=${image.uri.substring(5)}`;
            }
            const compressedImg = await manipulateAsync(image.uri, [{ resize }], {
                compress,
                format: SaveFormat.JPEG,
                base64: false,
            });
            image = { ...image, ...compressedImg };
            return {
                uri: image.uri,
                id: image.id,
                file: image,
            };
        })));
    };
    return (React.createElement(React.Fragment, null,
        Platform.OS === "web" && (React.createElement("input", { type: "file", onChange: handleWebChange, ref: inputRef, style: { display: "none" }, accept: "image/*", multiple: maxSelection > 1 })),
        React.createElement(TouchableOpacity, { testID: "inputImageTouchableOpacity", onPress: handlePress, style: [styles.default, userStyles] },
            React.createElement(View, null,
                showModal && (React.createElement(View, { testID: "inputImagePicker" },
                    React.createElement(Modal, { styles: [
                            modalStyles,
                            Platform.OS === "android"
                                ? styles.androidModalStyles
                                : styles.iosModalStyles,
                        ], actions: React.createElement(MultipleImagePicker, { toggleShowModal: toggleShowModal, setImages: handleNativeChange, currentSelection: images.map(({ id }) => id), maxSelection: maxSelection }) }))),
                !images.length && (React.createElement(View, { style: styles.placeholderImage }, buttonImage ? (React.createElement(Image, { testID: "inputImageButtonImage", source: buttonImage, style: [
                        { maxWidth: buttonImageWidth || "auto" },
                        styles.buttonImage,
                    ] })) : (React.createElement(View, { style: styles.placeholderIconContainer },
                    React.createElement(Icon.Camera, { color: "greyscale", testID: "inputImageButtonIcon" }))))),
                images.length > 0 && (React.createElement(View, { style: styles.list }, images.map((image, index) => (React.createElement(View, { key: index, style: [
                        styles.imageContainer,
                        index === 0 && { paddingLeft: 0 },
                        index === images.length - 1 && { paddingRight: 0 },
                    ] },
                    React.createElement(TouchableOpacity, { style: [styles.icon, smallThumbnail && styles.smallIcon], onPress: () => removeImage(image.id) },
                        React.createElement(Icon.Trash, { width: smallThumbnail ? theme.spacing.small : 20, color: "dark" })),
                    React.createElement(Image, { source: { uri: image.uri }, style: [
                            styles.image,
                            smallThumbnail && styles.smallThumbnail,
                        ] }))))))),
            React.createElement(Text, { styles: [styles.label, buttonLabelStyles] }, buttonLabel))));
};
//# sourceMappingURL=InputImage.js.map